import React from 'react';

import type { LexicalNode } from '../types/lexical.types';
import { ReaderParagraph } from '../components/lexical-content/reader-paragraph';
import { getLexicalHeadingClassName } from './lexical-heading.util';
import { extractTextFromNode, isLexicalTextNode, renderTextNode } from './lexical-text.util';

export function renderLexicalNode(
  node: LexicalNode,
  key: number,
  textColor: string = '#111827',
): React.ReactNode {
  if (!node) return null;

  switch (node.type) {
    case 'heading': {
      const headingText = renderLexicalChildren(node.children, textColor);
      const tag = node.tag || 'h2';
      switch (tag) {
        case 'h1':
          return (
            <h1 key={key} className={getLexicalHeadingClassName(tag)} style={{ color: textColor }}>
              {headingText}
            </h1>
          );
        case 'h2':
          return (
            <h2 key={key} className={getLexicalHeadingClassName(tag)} style={{ color: textColor }}>
              {headingText}
            </h2>
          );
        case 'h3':
          return (
            <div key={key} className={getLexicalHeadingClassName(tag)} style={{ color: textColor }}>
              {headingText}
            </div>
          );
        case 'h4':
          return (
            <h4 key={key} className={getLexicalHeadingClassName(tag)} style={{ color: textColor }}>
              {headingText}
            </h4>
          );
        case 'h5':
          return (
            <h5 key={key} className={getLexicalHeadingClassName(tag)} style={{ color: textColor }}>
              {headingText}
            </h5>
          );
        case 'h6':
          return (
            <h6 key={key} className={getLexicalHeadingClassName(tag)} style={{ color: textColor }}>
              {headingText}
            </h6>
          );
        default:
          return (
            <h2 key={key} className={getLexicalHeadingClassName('h2')} style={{ color: textColor }}>
              {headingText}
            </h2>
          );
      }
    }

    case 'paragraph': {
      const paragraphContent = renderLexicalChildren(node.children, textColor);
      const paragraphText = extractTextFromNode(node);
      const paragraphId = `paragraph-${key}`;

      return (
        <ReaderParagraph
          key={`paragraph-wrapper-${key}`}
          paragraphId={paragraphId}
          index={key}
          textColor={textColor}
          paragraphText={paragraphText}
        >
          {paragraphContent}
        </ReaderParagraph>
      );
    }

    case 'list': {
      const ListTag = node.listType === 'number' ? 'ol' : 'ul';
      return (
        <ListTag
          key={key}
          className={
            node.listType === 'number'
              ? 'list-decimal list-inside mb-4'
              : 'list-disc list-inside mb-4'
          }
          style={{ color: textColor }}
        >
          {node.children?.map((child, idx) => renderLexicalNode(child, idx, textColor))}
        </ListTag>
      );
    }

    case 'listitem':
      return (
        <li key={key} className="mb-2" style={{ color: textColor }}>
          {renderLexicalChildren(node.children, textColor)}
        </li>
      );

    case 'quote':
      return (
        <blockquote
          key={key}
          className="border-l-4 border-gray-300 pl-4 italic mb-4"
          style={{ color: textColor, opacity: 0.8 }}
        >
          {renderLexicalChildren(node.children, textColor)}
        </blockquote>
      );

    case 'code':
      return (
        <pre key={key} className="bg-gray-100 rounded p-4 overflow-x-auto mb-4">
          <code>{renderLexicalChildren(node.children, textColor)}</code>
        </pre>
      );

    case 'text': {
      if (isLexicalTextNode(node)) {
        return renderTextNode(node, key, textColor);
      }
      return null;
    }

    case 'link':
    case 'autolink': {
      const href = node.url || node.href;
      if (!href) {
        return (
          <React.Fragment key={key}>
            {renderLexicalChildren(node.children, textColor)}
          </React.Fragment>
        );
      }

      return (
        <a
          key={key}
          href={href}
          target={node.target || '_blank'}
          rel={node.rel || 'noopener noreferrer'}
          className="underline text-blue-500 hover:text-blue-700"
          onClick={(e) => e.stopPropagation()}
        >
          {renderLexicalChildren(node.children, textColor)}
        </a>
      );
    }

    case 'linebreak':
      return <br key={key} />;

    case 'image': {
      const src = node.src || node.url;
      if (!src) {
        return null;
      }
      const align = node.align === 'left' || node.align === 'right' ? node.align : 'center';
      const alignClass =
        align === 'left' ? 'mr-auto ml-0' : align === 'right' ? 'ml-auto mr-0' : 'mx-auto';
      return (
        <figure key={key} className="my-6 block w-full">
          <img
            src={src}
            alt={node.altText || node.alt || ''}
            className={`block max-w-full h-auto rounded-md ${alignClass}`}
            loading="lazy"
          />
        </figure>
      );
    }

    default: {
      if (node.children) {
        return (
          <div key={key} style={{ color: textColor }}>
            {renderLexicalChildren(node.children, textColor)}
          </div>
        );
      }
      return null;
    }
  }
}

export function renderLexicalChildren(
  children: LexicalNode[] | undefined,
  textColor: string = '#111827',
): React.ReactNode[] {
  if (!Array.isArray(children)) return [];

  return children.map((child, idx) => {
    if (isLexicalTextNode(child)) {
      return renderTextNode(child, idx, textColor);
    }
    return renderLexicalNode(child, idx, textColor);
  });
}
